import {
	FensterDienst, GEOM_UMRISS_SCHLUESSEL, geomVergleich, KlaerReihe, klaereReihen,
	projektVergleich, UmrissKonfliktComponent, ZEICHNUNG_UMRISS_SCHLUESSEL, zeichnungsVergleich,
} from '@luzula/luzula-lib';
import { bohrpunktKonfliktVergleich } from './bohrpunkt-konflikt-vergleich';

/**
 * Alle Datensätze mit zwei Fassungen nacheinander klären.
 *
 * Ein Fenster je Datensatz, in der Reihenfolge der Meldung. Wer abbricht,
 * verliert nichts: Beide Fassungen bleiben stehen und tauchen beim nächsten Mal
 * wieder auf. Deshalb bricht ein Abbruch auch nicht die ganze Reihe ab.
 *
 * **Drei Speicher, drei Wege.** bpweb hat die Punkte, die Zeichnungen und die
 * Flächen des Karten-Editors — drei Datenbanken mit eigenen Kennungsbereichen.
 * Bis zum 29.08.2026 ließen sich nur die Punkte klären; die anderen beiden
 * wurden gemeldet und blieben stehen.
 *
 * Punkte gehen Feld für Feld. Zeichnungen und Flächen bekommen die Karte: Der
 * **Umriss** ist ein Ding und kein Satz von Werten — wer zwei Umrisse mischt,
 * bekommt einen dritten, den niemand gezeichnet hat.
 *
 * Steht hier und nicht in der `AppComponent`, damit es ohne Angular-Umgebung
 * geprüft werden kann.
 */

/**
 * Die Dienste, aus denen die offenen Fassungen kommen. Jeder darf fehlen.
 *
 * Das **Projekt** steht zuletzt: Seine Fassungen liegen auf dem Server, dafür
 * braucht es eine Verbindung. Wer offline klärt, soll wenigstens die drei
 * anderen Sorten erledigt haben, bevor er auf diese Wand trifft.
 */
export interface KlaerDienste {
	/** Der Bohrpunkt-Dienst (`documentConflicts`). */
	punkte?: any;
	/** Der Zeichnungs-Dienst (`zeichnungsKonflikte`). */
	zeichnungen?: any;
	/** Die Flächen des Karten-Editors (`documentConflicts`). */
	geometrien?: any;
	/** Die Projekt-Datenbank (`documentConflicts`). */
	projekt?: any;
}

/**
 * Die offenen Kennungen eines Dienstes.
 *
 * Der Zeichnungs-Dienst nennt sein Signal `zeichnungsKonflikte` — dort ist das
 * Wort auch richtig, es geht um Zeichnungen und nicht um Dokumente allgemein.
 * Beide Namen werden gelesen, statt einen Dienst umzubenennen.
 */
function offeneVon(dienst: any): string[] {
	const liste = dienst?.documentConflicts?.() ?? dienst?.zeichnungsKonflikte?.() ?? [];
	return liste.map((k: any) => k.docId).filter(Boolean);
}

/**
 * Stellt zusammen, was zu klären ist — je Sorte eine Reihe.
 *
 * Getrennt vom Durchgehen, damit sich prüfen lässt, welche Sorte welchen Weg
 * bekommt, ohne ein Fenster zu öffnen.
 */
export function klaerReihenFuer(dienste: KlaerDienste): KlaerReihe[] {
	const reihen: KlaerReihe[] = [];

	const punkte = offeneVon(dienste.punkte);
	if (punkte.length) {
		reihen.push({
			quelle: dienste.punkte,
			docIds: punkte,
			vergleich: bohrpunktKonfliktVergleich(),
			titel: 'Zwei Fassungen eines Punktes',
			einleitung: 'An diesem Punkt wurde gleichzeitig gearbeitet.',
		});
	}

	const zeichnungen = offeneVon(dienste.zeichnungen);
	if (zeichnungen.length) {
		reihen.push({
			quelle: dienste.zeichnungen,
			docIds: zeichnungen,
			vergleich: zeichnungsVergleich(),
			komponente: UmrissKonfliktComponent,
			umrissSchluessel: ZEICHNUNG_UMRISS_SCHLUESSEL,
			titel: 'Zwei Fassungen einer Zeichnung',
			einleitung: 'An dieser Zeichnung wurde gleichzeitig gearbeitet.',
		});
	}

	const geometrien = offeneVon(dienste.geometrien);
	if (geometrien.length) {
		reihen.push({
			quelle: dienste.geometrien,
			docIds: geometrien,
			vergleich: geomVergleich(),
			komponente: UmrissKonfliktComponent,
			umrissSchluessel: GEOM_UMRISS_SCHLUESSEL,
			titel: 'Zwei Fassungen einer Fläche',
			einleitung: 'An dieser Fläche des Karten-Editors wurde gleichzeitig gearbeitet.',
		});
	}

	const projekt = offeneVon(dienste.projekt);
	if (projekt.length) {
		reihen.push({
			quelle: dienste.projekt,
			docIds: projekt,
			vergleich: projektVergleich(),
			titel: 'Zwei Fassungen der Projekt-Angaben',
			einleitung: 'An den Angaben dieses Projekts wurde gleichzeitig gearbeitet.',
		});
	}

	return reihen;
}

/**
 * Geht alles durch, was offen ist, und gibt zurück, wie viele Fenster gezeigt
 * wurden.
 */
export async function klaereAlleFassungen(
	fenster: FensterDienst,
	dienste: KlaerDienste,
): Promise<number> {
	return klaereReihen(fenster, klaerReihenFuer(dienste));
}
